import type {
  FolderPathContractDto,
  MediaFileDetailsContractDto,
  MediaFileListItemContractDto,
  MediaFilePageContractDto,
  MediaFolderChildrenContractDto,
  MediaFolderListItemContractDto,
} from '@core/api/contracts';
import type {
  FolderPathViewModel,
  MediaFileDetailsViewModel,
  MediaFileListItemViewModel,
  MediaFilePageViewModel,
  MediaFolderChildrenViewModel,
  MediaFolderListItemViewModel,
} from '../models/media-browser.view-model';
import { toRenditionViewModel } from './media-attachments-owner.mapper';

export function toMediaFolderChildrenViewModel(dto: MediaFolderChildrenContractDto): MediaFolderChildrenViewModel {
  return {
    parentFolderId: required(dto.parentFolderId, 'MediaFolderChildrenDto.parentFolderId'),
    items: required(dto.items, 'MediaFolderChildrenDto.items').map(toMediaFolderListItemViewModel),
  };
}

export function toFolderPathViewModel(dto: FolderPathContractDto): FolderPathViewModel {
  return {
    folderId: required(dto.folderId, 'FolderPathViewModel.folderId'),
    path: required(dto.path, 'FolderPathViewModel.path').map((item) => ({
      id: required(item.id, 'FolderPathItemDto.id'),
      name: required(item.name, 'FolderPathItemDto.name'),
    })),
  };
}

export function toMediaFilePageViewModel(dto: MediaFilePageContractDto): MediaFilePageViewModel {
  return {
    content: required(dto.content, 'MediaFilePageViewModel.content').map(toMediaFileListItemViewModel),
    pageNumber: required(dto.pageNumber, 'MediaFilePageViewModel.pageNumber'),
    pageSize: required(dto.pageSize, 'MediaFilePageViewModel.pageSize'),
    totalElements: required(dto.totalElements, 'MediaFilePageViewModel.totalElements'),
    totalPages: required(dto.totalPages, 'MediaFilePageViewModel.totalPages'),
    first: required(dto.first, 'MediaFilePageViewModel.first'),
    last: required(dto.last, 'MediaFilePageViewModel.last'),
  };
}

export function toMediaFileDetailsViewModel(dto: MediaFileDetailsContractDto): MediaFileDetailsViewModel {
  return {
    id: required(dto.id, 'MediaFileDetailsViewModel.id'),
    type: required(dto.type, 'MediaFileDetailsViewModel.type'),
    status: required(dto.status, 'MediaFileDetailsViewModel.status'),
    originalFileName: required(dto.originalFileName, 'MediaFileDetailsViewModel.originalFileName'),
    contentType: required(dto.contentType, 'MediaFileDetailsViewModel.contentType'),
    sizeBytes: required(dto.sizeBytes, 'MediaFileDetailsViewModel.sizeBytes'),
    widthPx: dto.widthPx ?? null,
    heightPx: dto.heightPx ?? null,
    sha256Hex: dto.sha256Hex ?? null,
    etag: dto.etag ?? null,
    previewUrl: dto.previewUrl ?? null,
    downloadUrl: dto.downloadUrl ?? null,
    renditions: required(dto.renditions, 'MediaFileDetailsViewModel.renditions').map(toRenditionViewModel),
    publicDeliveryStatus: dto.publicDeliveryStatus,
  };
}

function toMediaFolderListItemViewModel(dto: MediaFolderListItemContractDto): MediaFolderListItemViewModel {
  return {
    id: required(dto.id, 'MediaFolderListItemViewModel.id'),
    parentId: dto.parentId ?? null,
    investmentId: dto.investmentId ?? null,
    name: required(dto.name, 'MediaFolderListItemViewModel.name'),
    sortOrder: required(dto.sortOrder, 'MediaFolderListItemViewModel.sortOrder'),
    system: required(dto.system, 'MediaFolderListItemViewModel.system'),
    systemKey: dto.systemKey ?? null,
    locked: required(dto.locked, 'MediaFolderListItemViewModel.locked'),
    childrenCount: required(dto.childrenCount, 'MediaFolderListItemViewModel.childrenCount'),
    filesCount: required(dto.filesCount, 'MediaFolderListItemViewModel.filesCount'),
  };
}

function toMediaFileListItemViewModel(dto: MediaFileListItemContractDto): MediaFileListItemViewModel {
  return {
    id: required(dto.id, 'MediaFileListItemViewModel.id'),
    folderId: required(dto.folderId, 'MediaFileListItemViewModel.folderId'),
    type: required(dto.type, 'MediaFileListItemViewModel.type'),
    status: required(dto.status, 'MediaFileListItemViewModel.status'),
    originalFileName: required(dto.originalFileName, 'MediaFileListItemViewModel.originalFileName'),
    contentType: required(dto.contentType, 'MediaFileListItemViewModel.contentType'),
    sizeBytes: required(dto.sizeBytes, 'MediaFileListItemViewModel.sizeBytes'),
    widthPx: dto.widthPx ?? null,
    heightPx: dto.heightPx ?? null,
    isRendition: required(dto.isRendition, 'MediaFileListItemViewModel.isRendition'),
    createdDate: required(dto.createdDate, 'MediaFileListItemViewModel.createdDate'),
    etag: dto.etag ?? null,
    previewUrl: dto.previewUrl ?? null,
    thumbnail: dto.thumbnail ? toRenditionViewModel(dto.thumbnail) : null,
  };
}

function required<T>(value: T | null | undefined, field: string): T {
  if (value === null || value === undefined) {
    throw new Error(`Invalid media API response: missing ${field}`);
  }
  return value;
}
